import { useEffect, useMemo } from 'react';
import { createShallowStore, createStore } from '@frontend/store';
import { sentry } from '@frontend/tracking';
import {
  useGetSelectedCalendarDateAppointmentInfo,
  useGetSelectedCalendarDateAppointmentList,
} from '../hooks/use-get-selected-calendar-date-appointment-info';
import { ExtendedAppointment } from '../types';

type State = {
  selectedAppointments: ExtendedAppointment[];
  selectedAppointment?: ExtendedAppointment;
  selectedAppointmentId?: string;
};

type Actions = {
  setSelectedAppointments: (appointments: ExtendedAppointment[]) => void;
  setSelectedAppointment: (appointment: ExtendedAppointment, reset?: boolean) => void;
  setSelectedAppointmentId: (appointmentId: string) => void;
  resetSelectedAppointment: () => void;
  resetSelection: () => void;
};

const initialState: State = {
  selectedAppointments: [],
};

type ScheduleAppointmentInfoPanelStore = State & Actions;

const useStore = createStore<ScheduleAppointmentInfoPanelStore>(
  (set) => ({
    ...initialState,
    setSelectedAppointments: (appointments) => {
      set({
        selectedAppointments: appointments,
        // auto select appointment if only one is selected
        selectedAppointment: appointments.length === 1 ? appointments[0] : undefined,
        selectedAppointmentId: appointments.length === 1 ? appointments[0].id : undefined,
      });
    },
    setSelectedAppointment: (appointment, reset = false) => {
      if (reset) {
        set({ selectedAppointment: appointment, selectedAppointmentId: appointment.id, selectedAppointments: [] });
      } else {
        set({ selectedAppointment: appointment, selectedAppointmentId: appointment.id });
      }
    },
    setSelectedAppointmentId: (appointmentId) => {
      set({ selectedAppointmentId: appointmentId, selectedAppointment: undefined, selectedAppointments: [] });
    },
    resetSelectedAppointment: () => set({ selectedAppointment: undefined, selectedAppointmentId: undefined }),
    resetSelection: () =>
      set({ selectedAppointments: [], selectedAppointment: undefined, selectedAppointmentId: undefined }),
  }),
  {
    name: 'ScheduleAppointmentInfoPanelStore',
    enabled: true,
    trace: true,
  }
);

export const useScheduleAppointmentInfoPanelShallowStore = createShallowStore(useStore);

export const useSetCalendarAppointmentInfoAsSelected = (
  eventId: string,
  locationId: string,
  cachedAppointments?: ExtendedAppointment[]
) => {
  const {
    selectedAppointment,
    selectedAppointmentId,
    setSelectedAppointment,
    setSelectedAppointmentId,
    resetSelection,
  } = useScheduleAppointmentInfoPanelShallowStore(
    'selectedAppointment',
    'selectedAppointmentId',
    'setSelectedAppointment',
    'setSelectedAppointmentId',
    'resetSelection'
  );
  // get appointment list based on calendar context & cached queries
  const apptInfoFromCalendar = useGetSelectedCalendarDateAppointmentInfo(eventId);

  // Memoize apptInfoFromCache to prevent creating new object references on every render
  const apptInfoFromCache = useMemo(() => {
    if (apptInfoFromCalendar) {
      return apptInfoFromCalendar;
    }
    return cachedAppointments?.find((appt) => appt.id === eventId);
    // SP-TODO: Find better way to handle deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apptInfoFromCalendar), JSON.stringify(cachedAppointments), eventId]);

  const isSelected = selectedAppointmentId === eventId;

  // need to sync selected appointment info if selected to keep data consistent on updates outside of this store
  useEffect(() => {
    if (isSelected && apptInfoFromCache) {
      setSelectedAppointment(apptInfoFromCache);
    }
  }, [isSelected, apptInfoFromCache, setSelectedAppointment]);

  return {
    isSelected,
    selectedAppointment,
    setAppointment: (reset?: boolean) => {
      if (apptInfoFromCache) {
        setSelectedAppointment(apptInfoFromCache, reset);
      } else {
        console.error('[useSetCalendarAppointmentInfoAsSelected] appointment info not found.');
        sentry.error({
          topic: 'schedule-calendar',
          team: 'schedule',
          error: `Appointment info not found.`,
          addContext: { name: 'details', context: { eventId, locationId } },
        });
        setSelectedAppointmentId(eventId);
      }
    },
    resetSelection,
  };
};

export const useSetCalendarAppointmentListAsSelected = (eventIds: string[]) => {
  const { setSelectedAppointments, selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedAppointments'
  );
  // get appointment list based on calendar context & cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(eventIds);

  const selectedAppointmentIds = useMemo(() => selectedAppointments.map((appt) => appt.id), [selectedAppointments]);

  const hasSelectedAppointmentsInList = useMemo(
    () => eventIds.some((eventId) => selectedAppointmentIds.includes(eventId)),
    [eventIds, selectedAppointmentIds]
  );

  const shouldSyncSelection = selectedAppointmentIds.length !== eventIds.length;

  // on resize of calendar view, sync current overlapping events
  useEffect(() => {
    if (hasSelectedAppointmentsInList && shouldSyncSelection) {
      setSelectedAppointments(appointments);
    }
  }, [appointments, hasSelectedAppointmentsInList, shouldSyncSelection, setSelectedAppointments]);

  return {
    isSelected: hasSelectedAppointmentsInList,
    selectedAppointments,
    setAppointments: () => setSelectedAppointments(appointments),
  };
};
